import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useEffect, useMemo, useRef, useState } from 'react';
import { isAllowedParentOrigin } from './config/appRegistry';
import { getConfiguredInteractionMode, loginRequest, SSO_SESSION_KEYS } from './config/msalConfig';
import { normalizeReturnPath, redirectParentWithError } from './utils/ssoNavigation';
import { isOAuthRedirectReturn } from './utils/oauthReturnUrl';

function readCallerOrigin() {
  const params = new URLSearchParams(window.location.search);
  return params.get('origin');
}

function readReturnUrl() {
  const params = new URLSearchParams(window.location.search);
  return params.get('returnUrl') || '/';
}

function readInteractionMode() {
  const q = new URLSearchParams(window.location.search).get('interaction');
  if (q) {
    const v = q.trim().toLowerCase();
    if (v === 'popup' || v === 'redirect') return v;
  }
  return getConfiguredInteractionMode();
}

function postToOpener(callerOrigin, type, payload) {
  if (!window.opener || window.opener.closed) return;
  if (!callerOrigin || !isAllowedParentOrigin(callerOrigin)) return;
  window.opener.postMessage({ type, ...payload }, callerOrigin);
}

export default function App() {
  const { instance, inProgress } = useMsal();
  const [error, setError] = useState(null);
  const interaction = useMemo(() => readInteractionMode(), []);
  const redirectStartedRef = useRef(false);

  const gate = useMemo(() => {
    const callerOrigin = readCallerOrigin();
    const returnPath = normalizeReturnPath(readReturnUrl());

    if (!callerOrigin) {
      return {
        ok: false,
        mode: interaction,
        callerOrigin: null,
        returnPath,
        blocked: 'Missing origin parameter. This page must be opened from Hermes sign-in.',
      };
    }
    if (!isAllowedParentOrigin(callerOrigin)) {
      return {
        ok: false,
        mode: interaction,
        callerOrigin,
        returnPath,
        blocked: 'Unauthorized caller origin.',
      };
    }

    if (interaction === 'popup') {
      if (!window.opener || window.opener.closed) {
        return {
          ok: false,
          mode: interaction,
          callerOrigin,
          returnPath,
          blocked: 'This window must be opened as a popup from Hermes (or use redirect mode).',
        };
      }
    }

    return { ok: true, mode: interaction, callerOrigin, returnPath, blocked: null };
  }, [interaction]);

  useEffect(() => {
    if (!gate.ok && gate.callerOrigin && gate.blocked === 'Unauthorized caller origin.') {
      if (gate.mode === 'popup') {
        postToOpener(gate.callerOrigin, 'SSO_AUTH_ERROR', {
          message: 'Unauthorized caller origin.',
        });
      } else {
        redirectParentWithError(gate.callerOrigin, gate.returnPath, 'Unauthorized caller origin.');
      }
    }
    if (!gate.ok && gate.callerOrigin && gate.blocked?.includes('popup')) {
      postToOpener(gate.callerOrigin, 'SSO_AUTH_ERROR', {
        message: 'Popup opener is not available.',
      });
    }
  }, [gate]);

  // Full-page redirect: MSAL loginRedirect (default).
  // Do not gate on inProgress alone — it can stay "startup" and never fire loginRedirect (stuck UI / perceived loop).
  // Index.js already awaited handleRedirectPromise(); await again here so loginRedirect never overlaps a pending redirect.
  useEffect(() => {
    if (!gate.ok || gate.mode !== 'redirect') return;
    // If the URL still carries ?code= / ?error= (e.g. redirect URI vs app config mismatch) but Hermes left ?origin= intact,
    // gate stays valid and loginRedirect would run again — Microsoft redirect loop. Send the user back with an error instead.
    if (isOAuthRedirectReturn()) {
      if (gate.callerOrigin) {
        redirectParentWithError(
          gate.callerOrigin,
          gate.returnPath,
          'Microsoft sign-in could not be completed on this page. Use Sign in with Microsoft again from Hermes.',
        );
      }
      return;
    }
    if (redirectStartedRef.current) return;
    redirectStartedRef.current = true;

    sessionStorage.setItem(SSO_SESSION_KEYS.parentOrigin, gate.callerOrigin);
    sessionStorage.setItem(SSO_SESSION_KEYS.returnPath, gate.returnPath);

    const callerOrigin = gate.callerOrigin;
    const returnPath = gate.returnPath;

    (async () => {
      try {
        await instance.handleRedirectPromise();
        // Let MsalProvider's handleRedirectPromise .finally() unblock inProgress before starting a new interaction.
        await new Promise((r) => setTimeout(r, 0));
        await instance.loginRedirect({
          ...loginRequest,
          prompt: 'select_account',
        });
      } catch (e) {
        const message =
          e?.errorMessage || e?.message || 'Microsoft sign-in could not start.';
        redirectParentWithError(callerOrigin, returnPath, message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally omit inProgress to avoid re-trigger loops
  }, [gate, instance]);

  // Popup: nested MSAL popup from auth SPA
  useEffect(() => {
    if (!gate.ok || gate.mode !== 'popup') return;
    if (inProgress !== InteractionStatus.None) return;

    let cancelled = false;

    (async () => {
      try {
        const result = await instance.loginPopup({
          ...loginRequest,
          prompt: 'select_account',
        });
        if (cancelled) return;
        const idToken = result.idToken;
        if (!idToken) {
          throw new Error('No id_token returned from Microsoft.');
        }
        postToOpener(gate.callerOrigin, 'SSO_AUTH_SUCCESS', { idToken });
        window.setTimeout(() => window.close(), 250);
      } catch (e) {
        if (cancelled) return;
        const message =
          e?.errorMessage || e?.message || 'Microsoft sign-in did not complete.';
        setError(message);
        postToOpener(gate.callerOrigin, 'SSO_AUTH_ERROR', { message });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [gate, instance, inProgress]);

  if (!gate.ok) {
    return (
      <div style={wrapStyle}>
        <p style={{ color: '#842029', maxWidth: 420, textAlign: 'center' }}>{gate.blocked}</p>
      </div>
    );
  }

  if (gate.mode === 'redirect') {
    return (
      <div style={wrapStyle}>
        <p style={{ fontSize: 16, color: '#333' }}>Redirecting to Microsoft…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={wrapStyle}>
        <p style={{ color: '#842029', maxWidth: 420, textAlign: 'center' }}>{error}</p>
        <p style={{ fontSize: 14, color: '#555' }}>You can close this window.</p>
      </div>
    );
  }

  return (
    <div style={wrapStyle}>
      <p style={{ fontSize: 16, color: '#333' }}>Signing in with Microsoft…</p>
    </div>
  );
}

const wrapStyle = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'system-ui, sans-serif',
  padding: 24,
  boxSizing: 'border-box',
};
